import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"

import Badge from "@kit/design/badge"
import Icon from "@kit/design/icon"
import Modal, { Action } from "@kit/design/modal"
import Stack from "@kit/design/stack"
import { toast } from "@kit/design/toast"

import { analytics } from "utils/analytics"
import { optIntoSignals } from "sdk/signals"
import { SIGNALS_TERMS, CURRENT_SIGNALS_TERMS_VERSION } from "./signals-terms"
import { SignalsWhatToExpectStepper } from "./signals-what-to-expect-stepper"

export const SignalsOptInModal = ({
  onOptInSuccess,
  onSkip,
  onClose,
  isPostUpgrade
}) => {
  const [isPending, setIsPending] = useState(false)
  const modalLabel = "Turn on Subscriber Signals"

  useEffect(() => {
    analytics.track("Element", "Opened", {
      element_type: "modal",
      name: "signals_opt_in_modal",
      label: modalLabel,
      is_post_upgrade: isPostUpgrade
    })
  }, [])

  const handleSkip = () => {
    analytics.track("Element", "Clicked", {
      element_type: "button",
      name: "signals_opt_in_no",
      text: "Maybe later",
      is_post_upgrade: isPostUpgrade
    })
    onSkip()
  }

  const handleOptIn = async () => {
    analytics.track("Element", "Clicked", {
      element_type: "button",
      name: "signals_opt_in_yes",
      text: "Yes, I want Signals",
      is_post_upgrade: isPostUpgrade
    })
    setIsPending(true)

    try {
      await optIntoSignals(CURRENT_SIGNALS_TERMS_VERSION)
      onOptInSuccess()
    } catch (error) {
      if (typeof window !== "undefined" && window.SentryClient) {
        window.SentryClient.captureException(error)
      }
      toast({
        type: "error",
        text: "Sorry, we couldn't turn on Subscriber Signals. If this error persists, please contact support."
      })
    }

    setIsPending(false)
  }

  return (
    <Modal
      label={modalLabel}
      onClose={onClose}
      width="600px"
      data-testid="signals-opt-in-modal"
      telemetry={false}
    >
      <Stack spacing={8} className="items-start">
        <div className="flex flex-col gap-2">
          <Badge color="darkBlue" className="self-start">
            <Icon name="diamond" size="xs" data-icon="inline-start" />
            New Pro feature
          </Badge>
          <p className="text-gray-900 text-sm">
            Subscriber Signals shows your subscribers&apos; demographic data and
            social influence to unlock more business opportunities.{" "}
            <a
              className="underline whitespace-nowrap"
              href="/signals"
              target="_blank"
              rel="noreferrer"
            >
              Learn more
              <Icon name="arrow-up-right" className="inline ml-1" />
            </a>
          </p>
        </div>

        <SignalsWhatToExpectStepper />

        <p className="text-gray-700 text-xs">
          {SIGNALS_TERMS[CURRENT_SIGNALS_TERMS_VERSION]}
        </p>
      </Stack>

      <Action variant="secondary" onClick={handleSkip} disabled={isPending}>
        Maybe later
      </Action>
      <Action onClick={handleOptIn} disabled={isPending}>
        Yes, I want Signals
      </Action>
    </Modal>
  )
}

SignalsOptInModal.propTypes = {
  onOptInSuccess: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isPostUpgrade: PropTypes.bool.isRequired
}
